import { motion, useTransform } from "framer-motion";
import { useScrollCtx } from "@/providers/AppProviders";
import { MRLogo } from "@/components/ui-custom/MRLogo";
import { BrowserCard } from "./BrowserCard";
import { ContactCard } from "./ContactCard";
import { Footer } from "./Footer";
import { content } from "@/lib/content";
import { ChevronDown } from "lucide-react";

const FrameLayer = ({ style, children, className = "" }) => (
  <motion.div
    style={style}
    className={`fixed inset-0 z-10 flex items-center px-6 pointer-events-none sm:px-10 lg:px-20 ${className}`}
  >
    {children}
  </motion.div>
);

export const Overlays = () => {
  const { progress } = useScrollCtx();

  // Frames 1 + 2 — persistent logo
  const logoOpacity = useTransform(progress, [0, 0.22, 0.3], [1, 1, 0]);
  const logoY = useTransform(progress, [0, 0.16, 0.3], [0, -30, -70]);
  const logoScale = useTransform(progress, [0, 0.16, 0.3], [1, 1, 0.8]);
  const hintOpacity = useTransform(progress, [0, 0.03, 0.08], [1, 1, 0]);
  const tagOpacity = useTransform(progress, [0.15, 0.2, 0.27, 0.33], [0, 1, 1, 0]);
  const tagY = useTransform(progress, [0.15, 0.21], [24, 0]);

  // Frame 3
  const f3 = useTransform(progress, [0.3, 0.355, 0.45, 0.49], [0, 1, 1, 0]);
  const f3LeftX = useTransform(progress, [0.3, 0.375], [-40, 0]);
  const browserScale = useTransform(progress, [0.3, 0.4, 0.49], [0.9, 1.05, 0.97]);

  // Frame 4
  const f4 = useTransform(progress, [0.5, 0.555, 0.61, 0.655], [0, 1, 1, 0]);
  const objX = useTransform(progress, [0.5, 0.575], [-70, 0]);
  const objRightOpacity = useTransform(progress, [0.52, 0.59], [0, 1]);
  const objRightX = useTransform(progress, [0.52, 0.59], [50, 0]);

  // Frame 5
  const f5 = useTransform(progress, [0.66, 0.715, 0.78, 0.815], [0, 1, 1, 0]);
  const f5Y = useTransform(progress, [0.66, 0.72], [20, 0]);

  // Frame 6
  const f6 = useTransform(progress, [0.82, 0.885, 0.95, 1], [0, 1, 1, 1]);
  const f6LogoY = useTransform(progress, [0.82, 0.9], [28, 0]);
  const f6CardY = useTransform(progress, [0.84, 0.93], [60, 0]);
  const f6PE = useTransform(
    progress,
    [0, 0.85, 0.865, 1],
    ["none", "none", "auto", "auto"]
  );

  return (
    <>
      {/* Frames 1 + 2 */}
      <FrameLayer style={{ opacity: logoOpacity }} className="justify-center">
        <div className="flex flex-col items-center text-center">
          <motion.div style={{ y: logoY, scale: logoScale }} className="text-primary">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: 0.35 }}
            >
              <MRLogo className="w-[137px] sm:w-[162px] lg:w-[187px]" />
            </motion.div>
          </motion.div>
          <motion.div
            style={{ opacity: tagOpacity, y: tagY }}
            className="mt-10 max-w-xl"
          >
            <p className="text-plate px-7 py-5 text-base font-normal leading-relaxed text-foreground sm:text-lg">
              {content.frame2.tagline}
            </p>
          </motion.div>
        </div>
      </FrameLayer>

      {/* scroll hint */}
      <motion.div
        style={{ opacity: hintOpacity }}
        className="fixed bottom-10 left-1/2 z-10 flex -translate-x-1/2 flex-col items-center gap-2 text-muted-foreground pointer-events-none"
      >
        <span className="text-[11px] uppercase tracking-[0.3em]">
          {content.scrollHint}
        </span>
        <ChevronDown className="h-4 w-4 animate-bounce" />
      </motion.div>

      {/* Frame 3 — split: text left + 3D browser right */}
      <FrameLayer style={{ opacity: f3 }} className="justify-center">
        <div className="flex w-full max-w-7xl flex-col items-center gap-10 lg:flex-row lg:items-center lg:justify-between">
          <motion.div
            style={{ x: f3LeftX }}
            className="order-2 max-w-md text-center lg:order-1 lg:text-left"
          >
            <div className="text-plate px-7 py-6">
              <span className="text-xs font-semibold uppercase tracking-[0.3em] text-primary">
                {content.frame3.kicker}
              </span>
              <p className="mt-4 font-display text-2xl font-medium leading-snug text-foreground sm:text-3xl">
                {content.frame3.text}
              </p>
            </div>
          </motion.div>
          <motion.div style={{ scale: browserScale }} className="order-1 lg:order-2">
            <BrowserCard />
          </motion.div>
        </div>
      </FrameLayer>

      {/* Frame 4 — globe + objective (text pinned to the edges) */}
      <FrameLayer style={{ opacity: f4 }} className="justify-center">
        <div className="flex w-full flex-col items-center gap-8 text-center lg:flex-row lg:items-center lg:justify-between lg:text-left">
          <motion.div style={{ x: objX }} className="lg:max-w-md">
            <h2 className="text-plate inline-block whitespace-pre-line px-7 py-5 font-display text-5xl leading-[1.04] text-primary sm:text-6xl lg:text-7xl">
              {content.frame4.title}
            </h2>
          </motion.div>
          <motion.div
            style={{ opacity: objRightOpacity, x: objRightX }}
            className="lg:max-w-sm"
          >
            <p className="text-plate px-7 py-6 text-base leading-relaxed text-foreground sm:text-lg lg:text-right">
              {content.frame4.text}
            </p>
          </motion.div>
        </div>
      </FrameLayer>

      {/* Frame 5 — globe close caption */}
      <FrameLayer style={{ opacity: f5 }} className="justify-center">
        <motion.div style={{ y: f5Y }} className="text-center">
          <div className="text-plate inline-block px-10 py-7">
            <span className="text-xs font-semibold uppercase tracking-[0.35em] text-primary">
              {content.frame5.kicker}
            </span>
            <p className="mx-auto mt-3 max-w-2xl font-display text-3xl font-medium text-foreground sm:text-4xl">
              {content.frame5.text}
            </p>
          </div>
        </motion.div>
      </FrameLayer>

      {/* Frame 6 — skyline + contact */}
      <motion.div
        style={{ opacity: f6 }}
        className="fixed inset-0 z-10 flex flex-col px-6 pointer-events-none sm:px-10"
      >
        <motion.div
          style={{ y: f6LogoY }}
          className="flex justify-center pt-24 text-primary sm:pt-28"
        >
          <MRLogo className="w-[113px] sm:w-[125px]" />
        </motion.div>
        <div className="flex flex-1 items-center justify-center">
          <motion.div
            style={{ y: f6CardY, pointerEvents: f6PE }}
            className="w-full max-w-md"
          >
            <ContactCard />
          </motion.div>
        </div>
        <motion.div style={{ pointerEvents: f6PE }}>
          <Footer />
        </motion.div>
      </motion.div>
    </>
  );
};
